import { useEffect, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  CheckCircle2,
  Clock,
  FilePlus,
  FileText,
  Loader2,
  Trash2,
  Upload,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import AppHeader from '../components/AppHeader';

const SLOTS = [
  {
    key: 'acceptance_letter',
    title: 'Acceptance letter or proof of enrollment',
    sub: 'Recommended — biggest single boost to your Trust Score.',
    optional: false,
  },
  {
    key: 'gov_id',
    title: 'Passport or government ID',
    sub: 'Recommended.',
    optional: false,
  },
  {
    key: 'study_permit',
    title: 'Study permit or visa',
    sub: 'Optional — only relevant for international students.',
    optional: true,
  },
  {
    key: 'proof_of_funds',
    title: 'Proof of funds',
    sub: 'Optional — bank statement, GIC, or guarantor letter.',
    optional: true,
  },
];

const STATUS_BADGE = {
  pending: { icon: Clock, color: 'text-amber-700', bg: 'bg-amber-50', label: 'Pending review' },
  verified: {
    icon: CheckCircle2,
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    label: 'Verified',
  },
  failed: { icon: AlertCircle, color: 'text-red-700', bg: 'bg-red-50', label: 'Needs attention' },
};

const ProfileDocuments = () => {
  const { api } = useAuth();
  const { bundle: ctxBundle, setBundle: setCtxBundle } = useProfile();
  const [bundle, setBundleLocal] = useState(ctxBundle);
  const setBundle = (b) => {
    setBundleLocal(b);
    setCtxBundle(b);
  };
  const [loading, setLoading] = useState(!ctxBundle);
  const [busySlot, setBusySlot] = useState(null);
  const [refDialogOpen, setRefDialogOpen] = useState(false);
  const [refForm, setRefForm] = useState({
    reference_number: 1,
    name: '',
    relationship: '',
    email: '',
    phone: '',
  });
  const [refSaving, setRefSaving] = useState(false);
  const [refError, setRefError] = useState('');
  const fileInputs = useRef({});

  const fetchBundle = async () => {
    if (ctxBundle) {
      setBundle(ctxBundle);
      setLoading(false);
      return;
    }
    const r = await api.get('/users/me');
    setBundle(r.data);
    setLoading(false);
  };

  useEffect(() => {
    fetchBundle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (slot, file) => {
    if (!file) return;
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only PDF, JPG, or PNG.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Max 10 MB per file.');
      return;
    }
    setBusySlot(slot);
    try {
      const fd = new FormData();
      fd.append('slot', slot);
      fd.append('file', file);
      const res = await api.post('/users/me/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBundle(res.data);
      toast.success('Document uploaded.');
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't save that file.");
    } finally {
      setBusySlot(null);
    }
  };

  const remove = async (slot) => {
    setBusySlot(slot);
    try {
      const res = await api.delete(`/users/me/documents/${slot}`);
      setBundle(res.data);
      toast.success('Removed.');
    } finally {
      setBusySlot(null);
    }
  };

  const openReferenceDialog = (number) => {
    const existing = bundle?.references?.find((r) => r.reference_number === number);
    setRefForm(
      existing
        ? {
            reference_number: number,
            name: existing.name || '',
            relationship: existing.relationship || '',
            email: existing.email || '',
            phone: existing.phone || '',
          }
        : { reference_number: number, name: '', relationship: '', email: '', phone: '' }
    );
    setRefError('');
    setRefDialogOpen(true);
  };

  const saveReference = async (e) => {
    e.preventDefault();
    setRefError('');
    if (!refForm.name.trim()) return setRefError('Please add a name.');
    if (!refForm.relationship.trim()) return setRefError('Please add the relationship.');
    if (!/.+@.+\..+/.test(refForm.email)) return setRefError("Hmm, that email doesn't look right.");
    setRefSaving(true);
    try {
      const res = await api.post('/users/me/references', refForm);
      setBundle(res.data);
      setRefDialogOpen(false);
      toast.success('Reference saved.');
    } catch (err) {
      setRefError(err.response?.data?.detail || 'Could not save. Try again?');
    } finally {
      setRefSaving(false);
    }
  };

  const removeReference = async (number) => {
    const res = await api.delete(`/users/me/references/${number}`);
    setBundle(res.data);
    toast.success('Reference removed.');
  };

  if (loading || !bundle) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  const docs = bundle.documents || [];
  const refs = bundle.references || [];

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="profile-documents-page">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-5 py-6 space-y-5">
        <div>
          <h2 className="text-2xl font-heading font-extrabold text-slate-900">
            Your document package
          </h2>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">
            Upload these once. We never share documents without your permission.
          </p>
        </div>

        {/* Document slots */}
        <div className="space-y-3" data-testid="document-slots">
          {SLOTS.map((slot) => {
            const doc = docs.find((d) => d.slot === slot.key);
            const status = doc?.verification_status;
            const Badge = status ? STATUS_BADGE[status] : null;
            return (
              <Card key={slot.key} className="bg-white border-slate-200/70 shadow-soft">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      {doc ? (
                        <FileText className="h-5 w-5 text-amber-600" />
                      ) : (
                        <FilePlus className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-sm text-slate-900">
                        {slot.title}
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">{slot.sub}</p>

                      {doc && (
                        <div className="mt-2 text-xs text-slate-700">
                          <p className="truncate font-medium">{doc.filename}</p>
                          <p className="text-slate-500">
                            Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                          </p>
                          {Badge && (
                            <span
                              className={`inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full ${Badge.bg} ${Badge.color}`}
                            >
                              <Badge.icon className="h-3 w-3" /> {Badge.label}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2">
                        <input
                          type="file"
                          ref={(el) => (fileInputs.current[slot.key] = el)}
                          accept="application/pdf,image/jpeg,image/png"
                          onChange={(e) => upload(slot.key, e.target.files?.[0])}
                          className="hidden"
                          data-testid={`doc-input-${slot.key}`}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-slate-300"
                          onClick={() => fileInputs.current[slot.key]?.click()}
                          disabled={busySlot === slot.key}
                          data-testid={`doc-upload-btn-${slot.key}`}
                        >
                          {busySlot === slot.key ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-1.5" />
                              {doc ? 'Replace' : 'Upload'}
                            </>
                          )}
                        </Button>
                        {doc && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => remove(slot.key)}
                            disabled={busySlot === slot.key}
                            data-testid={`doc-remove-btn-${slot.key}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* References */}
        <div>
          <h3 className="font-heading font-bold text-base text-slate-900 mb-2">
            References (optional, up to 2)
          </h3>
          <div className="space-y-3" data-testid="reference-slots">
            {[1, 2].map((n) => {
              const r = refs.find((x) => x.reference_number === n);
              return (
                <Card key={n} className="bg-white border-slate-200/70 shadow-soft">
                  <CardContent className="p-4 sm:p-5">
                    {r ? (
                      <div>
                        <p className="font-heading font-semibold text-sm text-slate-900">
                          {r.name}{' '}
                          <span className="text-xs text-slate-500 font-normal">
                            · {r.relationship}
                          </span>
                        </p>
                        <p className="text-xs text-slate-600">{r.email}</p>
                        {r.phone && <p className="text-xs text-slate-600">{r.phone}</p>}
                        <div className="mt-3 flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-slate-300"
                            onClick={() => openReferenceDialog(n)}
                            data-testid={`ref-edit-${n}`}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => removeReference(n)}
                            data-testid={`ref-remove-${n}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full border-dashed border-slate-300"
                        onClick={() => openReferenceDialog(n)}
                        data-testid={`ref-add-${n}`}
                      >
                        Add reference {n}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Sharing preview */}
        <Card className="bg-amber-50/60 border-amber-200/60">
          <CardContent className="p-5">
            <p className="text-sm text-slate-700 leading-relaxed">
              <strong className="font-semibold">Sharing preview:</strong> when you message a
              landlord, you'll choose which documents to share. Nothing is shared automatically.
            </p>
            <Button
              disabled
              className="mt-3 w-full bg-slate-200 text-slate-500 cursor-not-allowed"
              data-testid="share-package-disabled"
            >
              Share package — available when you message a landlord
            </Button>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-500 text-center">
          We store these documents only to help you apply for housing. We never share without your
          explicit permission. Deleting your account purges them within 30 days.
        </p>
      </main>

      {/* Reference dialog */}
      <Dialog open={refDialogOpen} onOpenChange={setRefDialogOpen}>
        <DialogContent
          className="w-[calc(100%-2rem)] sm:max-w-md bg-card-cream border-0 shadow-xl p-5 sm:p-6"
          data-testid="reference-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-heading font-bold text-slate-900">
              Reference {refForm.reference_number}
            </DialogTitle>
            <DialogDescription className="text-slate-600 mt-1.5">
              A teacher, mentor, or previous landlord works well.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveReference} className="mt-4 space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Name</Label>
              <Input
                value={refForm.name}
                onChange={(e) => setRefForm({ ...refForm, name: e.target.value })}
                maxLength={80}
                className="h-12 bg-white"
                data-testid="ref-name"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Relationship</Label>
              <Input
                value={refForm.relationship}
                onChange={(e) => setRefForm({ ...refForm, relationship: e.target.value })}
                maxLength={80}
                placeholder="e.g. Professor, mentor, previous landlord"
                className="h-12 bg-white"
                data-testid="ref-relationship"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Email</Label>
              <Input
                type="email"
                value={refForm.email}
                onChange={(e) => setRefForm({ ...refForm, email: e.target.value })}
                className="h-12 bg-white"
                data-testid="ref-email"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Phone (optional)</Label>
              <Input
                type="tel"
                value={refForm.phone}
                onChange={(e) => setRefForm({ ...refForm, phone: e.target.value })}
                className="h-12 bg-white"
                data-testid="ref-phone"
              />
            </div>
            {refError && <p className="text-sm text-red-600">{refError}</p>}
            <Button
              type="submit"
              disabled={refSaving}
              className="w-full btn-primary py-6 text-base"
              data-testid="ref-save"
            >
              {refSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Save reference'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfileDocuments;
